import { Trans } from 'react-i18next'

import { useAppSelector } from '@/hooks/store.ts'
import { useHostProfileUrl } from '@/hooks/useHostProfileUrl.ts'
import { appSlice } from '@/store/appSlice'
import { configSelector } from '@/store/selectors/configSelector.ts'
import { getPolicyI18nKey } from '../../utils/getPolicyI18nKey.ts'
import { ExternalLink } from '../ExternalLink'

export interface TranslatedPolicyTextProps {
  beachPrivacyPolicy?: string
  regulation?: string
  onBookingPolicyDialogOpen(): void
  onRefundPolicyDialogOpen(): void
}

export const TranslatedPolicyText = (props: TranslatedPolicyTextProps) => {
  const config = useAppSelector(configSelector)
  const isPool = useAppSelector(appSlice.selectors.isPool)

  const policyI18nKey = getPolicyI18nKey({
    beachPrivacyPolicy: props.beachPrivacyPolicy,
    regulation: props.regulation,
  })

  const keySuffix = isPool ? '_pool' : '_beach'
  const tOptions = { brandName: config.brandName }

  const {
    OpenPrivacyPolicyAnchor,
    OpenBeachPrivacyPolicyAnchor,
    OpenTermsAnchor,
    OpenBookingPolicyDialogButton,
    OpenRefundPolicyDialogButton,
  } = usePolicyElements(props)

  switch (policyI18nKey) {
    case 'summary.privacy':
      return (
        <Trans i18nKey={`${policyI18nKey}${keySuffix}`} tOptions={tOptions}>
          {OpenPrivacyPolicyAnchor}
          {OpenBeachPrivacyPolicyAnchor}
          {OpenTermsAnchor}
          {OpenBookingPolicyDialogButton}
          {OpenRefundPolicyDialogButton}
        </Trans>
      )

    case 'summary.smallPrivacy':
      return (
        <Trans i18nKey={`${policyI18nKey}${keySuffix}`} tOptions={tOptions}>
          {OpenPrivacyPolicyAnchor}
          {OpenTermsAnchor}
          {OpenRefundPolicyDialogButton}
        </Trans>
      )

    case 'summary.privacyWithRegulation':
      return (
        <Trans i18nKey={`${policyI18nKey}${keySuffix}`} tOptions={tOptions}>
          {OpenPrivacyPolicyAnchor}
          {OpenTermsAnchor}
          {OpenBookingPolicyDialogButton}
          {OpenRefundPolicyDialogButton}
        </Trans>
      )

    case 'summary.privacyWithBeachPolicy':
      return (
        <Trans i18nKey={`${policyI18nKey}${keySuffix}`} tOptions={tOptions}>
          {OpenPrivacyPolicyAnchor}
          {OpenBeachPrivacyPolicyAnchor}
          {OpenTermsAnchor}
          {OpenRefundPolicyDialogButton}
        </Trans>
      )
  }
}

const usePolicyElements = (props: TranslatedPolicyTextProps) => {
  const { hostUrl } = useHostProfileUrl()

  const OpenPrivacyPolicyAnchor = (
    <ExternalLink href={`${hostUrl}/privacy-policy/`} isInsideParagraph />
  )

  const OpenBeachPrivacyPolicyAnchor = (
    <ExternalLink href={props.beachPrivacyPolicy} isInsideParagraph />
  )

  const OpenTermsAnchor = (
    <ExternalLink href={`${hostUrl}/terms/`} isInsideParagraph />
  )

  const OpenBookingPolicyDialogButton = (
    <ExternalLink
      as="button"
      isInsideParagraph
      onClick={props.onBookingPolicyDialogOpen}
      type="button"
    />
  )

  const OpenRefundPolicyDialogButton = (
    <ExternalLink
      as="button"
      isInsideParagraph
      onClick={props.onRefundPolicyDialogOpen}
      type="button"
    />
  )

  return {
    OpenBeachPrivacyPolicyAnchor,
    OpenBookingPolicyDialogButton,
    OpenPrivacyPolicyAnchor,
    OpenRefundPolicyDialogButton,
    OpenTermsAnchor,
  }
}
